import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "../lib/api";
import { fmtMoney, fmtNumber } from "../components/ops/opsUtils";
import { Input } from "../components/ui/input";
import { Search } from "lucide-react";

/**
 * Lightweight clients view — groups loans by canonical client_name.
 * Real client profile page can come in Phase B.
 */
export default function ClientsIndex() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/operations/loans?limit=2000");
      setLoans(data);
    } finally { setLoading(false); }
  })(); }, []);

  const clients = useMemo(() => {
    const map = new Map();
    for (const l of loans) {
      const key = (l.client_name || "").trim();
      if (!key) continue;
      const c = map.get(key) || { name: key, count: 0, total_principal: 0, active_payoff: 0, last_funding: null, attorneys: new Set() };
      c.count += 1;
      c.total_principal += l.principal || 0;
      if (["Funded", "Active", "PendingSettlement", "PayoffRequested"].includes(l.current_status))
        c.active_payoff += l.current_payoff || 0;
      if (!c.last_funding || l.funding_date > c.last_funding) c.last_funding = l.funding_date;
      if (l.attorney_email) c.attorneys.add(l.attorney_email);
      map.set(key, c);
    }
    const arr = [...map.values()].map((c) => ({ ...c, attorneys: [...c.attorneys] }));
    arr.sort((a, b) => b.active_payoff - a.active_payoff);
    const s = q.trim().toLowerCase();
    return s ? arr.filter((c) => c.name.toLowerCase().includes(s)) : arr;
  }, [loans, q]);

  if (loading) return <div className="p-10 text-[#6B7280]" data-testid="clients-loading">Loading clients…</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto" data-testid="clients-index">
      <div className="uppercase-label">Operations</div>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Clients</h1>
      <p className="text-sm text-[#6B7280] mt-1.5 mb-6">{fmtNumber(clients.length)} unique clients · grouped from all loans</p>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" data-testid="clients-search" />
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <th className="px-4 py-2.5 font-medium">Client</th>
              <th className="px-4 py-2.5 font-medium text-center">Loans</th>
              <th className="px-4 py-2.5 font-medium text-right">Total principal</th>
              <th className="px-4 py-2.5 font-medium text-right">Active exposure</th>
              <th className="px-4 py-2.5 font-medium">Last funded</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-[#6B7280]">No clients match.</td></tr>
            )}
            {clients.map((c, i) => (
              <tr key={c.name} data-testid={`client-row-${i}`}
                  onClick={() => (window.location.href = `/admin/clients/${encodeURIComponent(c.name)}`)}
                  className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors cursor-pointer">
                <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <Link to={`/admin/clients/${encodeURIComponent(c.name)}`} className="text-[#0A2540] font-medium hover:text-[#0066FF]">{c.name}</Link>
                  <div className="text-[11px] text-[#9CA3AF] truncate max-w-md">{c.attorneys.join(" · ")}</div>
                </td>
                <td className="px-4 py-2.5 text-center tabular-nums text-[#0A2540]">{c.count}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-[#0A2540]">{fmtMoney(c.total_principal)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-[#0066FF] font-medium">{fmtMoney(c.active_payoff)}</td>
                <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{formatDate(c.last_funding)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
